import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-md-editor/markdown-editor.css'
import '@uiw/react-markdown-preview/markdown.css'
import { GraduationCap, HelpCircle, History, Megaphone, MessageSquare, Plug, Receipt, Wrench } from 'lucide-react'
import { Button } from '@agms/ui'
import { useAuth, usePermissions } from '../hooks/useAuth'
import { CardTitle, PageHeader, SurfaceCard } from '../components/ui'
import { KnownIssueBanner } from '../components/help/KnownIssueBanner'
import { KnownIssuePinForm } from '../components/help/KnownIssuePinForm'
import { helpApi, type Article, type ArticleCategory, type ArticleComment, type ArticlePayload, type ArticleStatus, type ArticleVersion } from '../lib/api'

const STATUS_LABELS: Record<ArticleStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  published: 'Published',
  archived: 'Archived',
}

const ICONS = {
  GraduationCap,
  Receipt,
  Wrench,
  Plug,
  Megaphone,
  HelpCircle,
}

function statusClass(status: ArticleStatus): string {
  switch (status) {
    case 'published':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'pending_review':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'archived':
      return 'border-gray-300 bg-gray-100 text-gray-700'
    case 'draft':
      return 'border-blue-200 bg-blue-50 text-blue-700'
  }
}

function ticketsSolvedText(count: number): string {
  return `Solved ${count} ${count === 1 ? 'ticket' : 'tickets'}`
}

function canEditArticle(article: Article, userId: string | undefined, canReview: boolean): boolean {
  if (article.status === 'published') {
    return canReview
  }

  if (article.status === 'draft' || article.status === 'pending_review') {
    return article.author_id === userId || canReview
  }

  return false
}

function canDeleteArticle(article: Article, isSuperAdmin: boolean, isManagement: boolean): boolean {
  if (isSuperAdmin) {
    return true
  }

  return isManagement && (article.status === 'published' || article.status === 'archived')
}

function DeleteArticleDialog({
  title,
  onCancel,
  onConfirm,
  isPending,
}: {
  title: string
  onCancel: () => void
  onConfirm: () => void
  isPending: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-article-title"
        aria-describedby="delete-article-body"
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
      >
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 id="delete-article-title" className="text-lg font-semibold text-slate-900">Delete this article?</h2>
        </div>
        <div className="px-5 py-4">
          <p id="delete-article-body" className="text-sm text-slate-700">
            {title} will be removed from Help Hub.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <Button variant="secondary" autoFocus onClick={onCancel}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Deleting...' : 'Delete article'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function StatusPill({ status }: { status: ArticleStatus }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass(status)}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

function CategoryIcon({ category }: { category: ArticleCategory }) {
  const Icon = ICONS[(category.icon ?? 'HelpCircle') as keyof typeof ICONS] ?? HelpCircle
  return <Icon className="h-4 w-4" />
}

function useCategories() {
  return useQuery({
    queryKey: ['help-categories'],
    queryFn: helpApi.listCategories,
  })
}

function DiffViewer({ version, article }: { version: ArticleVersion; article: Article }) {
  const oldLines = version.body.split('\n')
  const newLines = article.body.split('\n')
  const max = Math.max(oldLines.length, newLines.length)

  return (
    <div className="mt-4 overflow-hidden rounded-md border border-slate-200 text-xs">
      <div className="grid grid-cols-2 bg-slate-50 font-semibold text-slate-600">
        <div className="border-r border-slate-200 px-3 py-2">Version {version.version_number}</div>
        <div className="px-3 py-2">Current</div>
      </div>
      {Array.from({ length: max }).map((_, index) => {
        const oldLine = oldLines[index] ?? ''
        const newLine = newLines[index] ?? ''
        const changed = oldLine !== newLine

        return (
          <div key={index} className="grid grid-cols-2 border-t border-slate-100">
            <pre className={`min-h-8 whitespace-pre-wrap border-r border-slate-100 px-3 py-2 ${changed ? 'bg-rose-50 text-rose-900' : 'bg-white text-slate-600'}`}>{oldLine || ' '}</pre>
            <pre className={`min-h-8 whitespace-pre-wrap px-3 py-2 ${changed ? 'bg-emerald-50 text-emerald-900' : 'bg-white text-slate-600'}`}>{newLine || ' '}</pre>
          </div>
        )
      })}
    </div>
  )
}

function CommentsPanel({ article, canResolve }: { article: Article; canResolve: boolean }) {
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const { data: comments = [] } = useQuery({
    queryKey: ['help-comments', article.slug],
    queryFn: () => helpApi.listComments(article.slug),
  })
  const postMutation = useMutation({
    mutationFn: () => helpApi.postComment(article.slug, body),
    onSuccess: () => {
      setBody('')
      queryClient.invalidateQueries({ queryKey: ['help-comments', article.slug] })
    },
  })
  const resolveMutation = useMutation({
    mutationFn: (commentId: string) => helpApi.resolveComment(article.slug, commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['help-comments', article.slug] }),
  })

  return (
    <SurfaceCard>
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-slate-500" />
        <CardTitle>Comments</CardTitle>
      </div>
      <div className="mt-4 space-y-3">
        {comments.length === 0 ? (
          <p className="text-sm text-slate-500">No comments yet.</p>
        ) : comments.map((comment: ArticleComment) => (
          <div key={comment.id} className="rounded-md border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-slate-900">{comment.user?.name ?? 'Unknown user'}</div>
                <div className="text-xs text-slate-500">{new Date(comment.created_at).toLocaleString()}</div>
              </div>
              {comment.resolved_at ? (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  Resolved
                </span>
              ) : canResolve ? (
                <Button variant="ghost" onClick={() => resolveMutation.mutate(comment.id)}>
                  Resolve
                </Button>
              ) : null}
            </div>
            <p className={`mt-3 whitespace-pre-wrap text-sm ${comment.resolved_at ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{comment.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <textarea
          aria-label="Comment or follow-up question"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Post a comment or ask a follow-up question..."
        />
        <Button className="mt-2" onClick={() => postMutation.mutate()} disabled={!body.trim() || postMutation.isPending}>
          Post Comment
        </Button>
      </div>
    </SurfaceCard>
  )
}

export function HelpHubPage() {
  const { hasRole } = usePermissions()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const { data: categories = [] } = useCategories()
  const { data, isLoading } = useQuery({
    queryKey: ['help-articles', { search, category }],
    queryFn: () => helpApi.listArticles({
      search: search || undefined,
      category: category || undefined,
      per_page: 24,
    }),
  })

  const articles = data?.data ?? []
  const canCreate = hasRole(['sales_rep', 'operations', 'management', 'super_admin'])
  const canSeeGaps = hasRole(['management', 'super_admin'])
  const canManageCategories = hasRole('super_admin')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Help"
        subtitle="Internal CRM playbooks, process notes, and support articles"
        action={canCreate ? (
          <div className="flex flex-wrap gap-2">
            {canManageCategories ? (
              <Link to="/help/categories">
                <Button variant="secondary">Manage Categories</Button>
              </Link>
            ) : null}
            {canSeeGaps ? (
              <Link to="/help/gaps">
                <Button variant="secondary">Knowledge Gaps</Button>
              </Link>
            ) : null}
            {canSeeGaps ? (
              <Link to="/help/health">
                <Button variant="secondary">Article Health</Button>
              </Link>
            ) : null}
            <Link to="/help/new">
              <Button>New article</Button>
            </Link>
          </div>
        ) : undefined}
      />

      <KnownIssueBanner />

      <SurfaceCard>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategory('')}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium ${category === '' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            All
          </button>
          {categories.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setCategory(item.slug)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${category === item.slug ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <CategoryIcon category={item} />
              {item.name}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <input
            aria-label="Search help articles"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search title, body, or tags..."
            className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </SurfaceCard>

      {isLoading ? (
        <SurfaceCard>
          <p className="py-12 text-center text-sm text-slate-500">Loading articles...</p>
        </SurfaceCard>
      ) : articles.length === 0 ? (
        <SurfaceCard>
          <p className="py-12 text-center text-sm text-slate-500">No Help Hub articles match this view.</p>
        </SurfaceCard>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {articles.map((article) => (
            <Link key={article.id} to={`/help/${article.slug}`} className="block">
              <SurfaceCard className="h-full transition hover:border-blue-200 hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {article.category ? <CategoryIcon category={article.category} /> : null}
                    {article.category?.name ?? 'Uncategorized'}
                  </div>
                  <StatusPill status={article.status} />
                </div>
                <h2 className="mt-4 text-base font-semibold text-slate-950">{article.title}</h2>
                <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">{article.excerpt ?? article.body}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                      {tag.name}
                    </span>
                  ))}
                </div>
                <div className="mt-4 text-xs text-slate-600">
                  {article.author?.name ?? 'Unknown'} · {article.view_count} views
                  {typeof article.tickets_solved_count === 'number' ? ` · ${ticketsSolvedText(article.tickets_solved_count)}` : ''}
                </div>
              </SurfaceCard>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function ArticleDetailPage() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { hasRole } = usePermissions()
  const [rejectionNote, setRejectionNote] = useState('')
  const [showVersions, setShowVersions] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<ArticleVersion | null>(null)
  const [feedbackComment, setFeedbackComment] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const { data: article, isLoading, error } = useQuery({
    queryKey: ['help-article', slug],
    queryFn: () => helpApi.getArticle(slug),
    enabled: !!slug,
  })

  const canReview = hasRole(['management', 'super_admin'])
  const canArchive = canReview && article?.status !== 'archived'
  const canRestore = canReview && article?.status === 'archived'
  const canEdit = !!article && canEditArticle(article, user?.id, canReview)
  const canDelete = !!article && canDeleteArticle(article, hasRole('super_admin'), hasRole('management'))
  const canResolveComments = !!article && (article.author_id === user?.id || canReview)
  const { data: versions = [] } = useQuery({
    queryKey: ['help-versions', slug],
    queryFn: () => helpApi.listVersions(slug),
    enabled: !!slug && showVersions,
  })

  const publishMutation = useMutation({
    mutationFn: () => helpApi.publishArticle(slug),
    onSuccess: (updated) => {
      queryClient.setQueryData(['help-article', slug], updated)
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
    },
  })
  const rejectMutation = useMutation({
    mutationFn: () => helpApi.rejectArticle(slug, rejectionNote),
    onSuccess: (updated) => {
      queryClient.setQueryData(['help-article', slug], updated)
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
    },
  })
  const archiveMutation = useMutation({
    mutationFn: () => helpApi.archiveArticle(slug),
    onSuccess: (updated) => {
      queryClient.setQueryData(['help-article', slug], updated)
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
    },
  })
  const restoreMutation = useMutation({
    mutationFn: () => helpApi.restoreArticle(slug),
    onSuccess: (updated) => {
      queryClient.setQueryData(['help-article', slug], updated)
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
    },
  })
  const deleteMutation = useMutation({
    mutationFn: () => helpApi.deleteArticle(slug),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['help-article', slug] })
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
      navigate('/help')
    },
  })
  const feedbackMutation = useMutation({
    mutationFn: (helpful: boolean) => helpApi.submitFeedback(slug, {
      helpful,
      comment: helpful ? null : feedbackComment || null,
    }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['help-article', slug], updated)
      setFeedbackComment('')
    },
  })

  if (isLoading) {
    return <SurfaceCard><p className="py-12 text-center text-sm text-slate-500">Loading article...</p></SurfaceCard>
  }

  if (error || !article) {
    return <SurfaceCard><p className="py-12 text-center text-sm text-rose-600">Article not found or unavailable.</p></SurfaceCard>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={article.title}
        subtitle={`${article.category?.name ?? 'Help Hub'} · ${article.author?.name ?? 'Unknown author'}`}
        action={(
          <div className="flex flex-wrap gap-2">
            <Link to="/help"><Button variant="secondary">Back</Button></Link>
            {canEdit ? <Link to={`/help/${article.slug}/edit`}><Button variant="secondary">Edit</Button></Link> : null}
            <Button variant="secondary" onClick={() => setShowVersions((value) => !value)}>
              Version history ({article.versions_count})
            </Button>
            {canArchive ? <Button variant="secondary" onClick={() => archiveMutation.mutate()}>Archive</Button> : null}
            {canRestore ? (
              <Button variant="secondary" onClick={() => restoreMutation.mutate()} disabled={restoreMutation.isPending}>
                Restore
              </Button>
            ) : null}
            {canDelete ? (
              <Button variant="destructive" onClick={() => setConfirmingDelete(true)}>Delete</Button>
            ) : null}
          </div>
        )}
      />

      <SurfaceCard>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <StatusPill status={article.status} />
          <span className="text-sm text-slate-500">{article.view_count} views</span>
          {typeof article.tickets_solved_count === 'number' ? (
            <span className="text-sm text-slate-500">{ticketsSolvedText(article.tickets_solved_count)}</span>
          ) : null}
          {article.tags.map((tag) => (
            <span key={tag.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {tag.name}
            </span>
          ))}
        </div>
        {article.ai_draft && article.status === 'draft' ? (
          <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <div className="font-semibold">AI draft</div>
            <p className="mt-1">This draft was written from a resolved ticket. It is not published until someone reviews it.</p>
            {canReview ? (
              <Button className="mt-3" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
                Approve & Publish
              </Button>
            ) : null}
          </div>
        ) : null}
        {article.rejected_at && article.author_id === user?.id ? (
          <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <div className="font-semibold">Rejected by reviewer</div>
            <p className="mt-1">{article.rejection_note}</p>
          </div>
        ) : null}
        <div data-color-mode="light" className="prose max-w-none">
          <MDEditor.Markdown source={article.body} />
        </div>
        <div className="mt-8 border-t border-slate-100 pt-4 text-sm text-slate-500">
          Last updated {new Date(article.updated_at).toLocaleString()}
          {article.published_at ? ` · Published ${new Date(article.published_at).toLocaleString()}` : ''}
        </div>
      </SurfaceCard>

      {canReview ? <KnownIssuePinForm slug={article.slug} status={article.status} /> : null}

      {showVersions ? (
        <SurfaceCard>
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" />
            <CardTitle>Version History</CardTitle>
          </div>
          {versions.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No saved versions yet. Versions are created when an article is edited.</p>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-[260px_1fr]">
              <div className="space-y-2">
                {versions.map((version) => (
                  <button
                    key={version.id}
                    type="button"
                    onClick={() => setSelectedVersion(version)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm ${selectedVersion?.id === version.id ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    <div className="font-medium text-slate-900">Version {version.version_number}</div>
                    <div className="text-xs text-slate-500">{new Date(version.created_at).toLocaleString()}</div>
                    {version.change_note ? <div className="mt-1 text-xs text-slate-600">{version.change_note}</div> : null}
                  </button>
                ))}
              </div>
              <div>
                {selectedVersion ? (
                  <>
                    <div className="rounded-md bg-slate-50 p-3 text-sm">
                      <div className="font-medium text-slate-900">{selectedVersion.title}</div>
                      <div className="text-xs text-slate-500">Edited by {selectedVersion.editor?.name ?? 'Unknown'}</div>
                    </div>
                    <DiffViewer version={selectedVersion} article={article} />
                  </>
                ) : (
                  <p className="rounded-md border border-slate-200 p-6 text-sm text-slate-500">Select a version to compare it with the current article.</p>
                )}
              </div>
            </div>
          )}
        </SurfaceCard>
      ) : null}

      {article.status === 'published' ? (
        <SurfaceCard>
          <CardTitle>Was this helpful?</CardTitle>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={() => feedbackMutation.mutate(true)} disabled={feedbackMutation.isPending}>
              Yes ({article.helpful_count})
            </Button>
            <Button variant="secondary" onClick={() => feedbackMutation.mutate(false)} disabled={feedbackMutation.isPending}>
              No ({article.not_helpful_count})
            </Button>
          </div>
          <textarea
            aria-label="Helpful feedback note"
            value={feedbackComment}
            onChange={(event) => setFeedbackComment(event.target.value)}
            className="mt-3 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Optional note when marking not helpful..."
          />
        </SurfaceCard>
      ) : null}

      <CommentsPanel article={article} canResolve={canResolveComments} />

      {confirmingDelete ? (
        <DeleteArticleDialog
          title={article.title}
          isPending={deleteMutation.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => deleteMutation.mutate()}
        />
      ) : null}

      {canReview && article.status === 'pending_review' ? (
        <SurfaceCard>
          <CardTitle>Review</CardTitle>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>Approve & Publish</Button>
          </div>
          <div className="mt-5">
            <label htmlFor="help-rejection-note" className="text-sm font-medium text-slate-700">Rejection note</label>
            <textarea
              id="help-rejection-note"
              value={rejectionNote}
              onChange={(event) => setRejectionNote(event.target.value)}
              className="mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Explain what needs to change before publishing..."
            />
            <Button
              variant="secondary"
              onClick={() => rejectMutation.mutate()}
              disabled={!rejectionNote.trim() || rejectMutation.isPending}
            >
              Reject
            </Button>
          </div>
        </SurfaceCard>
      ) : null}
    </div>
  )
}

interface ArticleEditorPageProps {
  mode: 'create' | 'edit'
}

export function ArticleEditorPage({ mode }: ArticleEditorPageProps) {
  const { slug = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { hasRole, canManageHelpHub } = usePermissions()
  const { data: categories = [] } = useCategories()
  const { data: article } = useQuery({
    queryKey: ['help-article', slug],
    queryFn: () => helpApi.getArticle(slug),
    enabled: mode === 'edit' && !!slug,
  })

  const requestedCategory = mode === 'create' ? (searchParams.get('category') ?? '') : ''
  const requestedTitle = mode === 'create' ? (searchParams.get('title') ?? '') : ''
  const [title, setTitle] = useState(requestedTitle)
  const [categoryId, setCategoryId] = useState(requestedCategory)
  const [body, setBody] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [tags, setTags] = useState('')
  const [changeNote, setChangeNote] = useState('')

  useEffect(() => {
    if (article) {
      setTitle(article.title)
      setCategoryId(article.category_id)
      setBody(article.body)
      setExcerpt(article.excerpt ?? '')
      setTags(article.tags.map((tag) => tag.name).join(', '))
    }
  }, [article])

  useEffect(() => {
    if (categories.length === 0) return
    if (categories.some((category) => category.id === categoryId)) return
    const requested = categories.find((category) => category.id === requestedCategory)
    setCategoryId(requested?.id ?? categories[0].id)
  }, [categories, categoryId, requestedCategory])

  const payload = (): ArticlePayload => {
    const base: ArticlePayload = {
      title,
      category_id: categoryId,
      body,
      excerpt: excerpt || null,
      visibility_roles: ['all'],
      tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    }

    if (mode === 'edit') {
      base.change_note = changeNote || null
    }

    return base
  }

  const saveArticle = async (): Promise<Article> => {
    if (mode === 'edit') {
      return helpApi.updateArticle(slug, payload())
    }

    return helpApi.createArticle(payload())
  }

  const saveMutation = useMutation({
    mutationFn: saveArticle,
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
      navigate(`/help/${saved.slug}`)
    },
  })

  const submitMutation = useMutation({
    mutationFn: async () => {
      const saved = await saveArticle()
      return helpApi.submitArticle(saved.slug)
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
      navigate(`/help/${saved.slug}`)
    },
  })

  const publishMutation = useMutation({
    mutationFn: async () => {
      const saved = await saveArticle()
      return helpApi.publishArticle(saved.slug)
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] })
      navigate(`/help/${saved.slug}`)
    },
  })

  const canReview = hasRole(['management', 'super_admin'])
  const canPublish = hasRole(['operations', 'management', 'super_admin'])
  const editingPublished = mode === 'edit' && article?.status === 'published'
  const disabled = !title.trim() || !categoryId || !body.trim()
  const noCategories = categories.length === 0

  if (mode === 'edit' && article && !canEditArticle(article, user?.id, canReview)) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit Help Article" subtitle="This article is locked" />
        <SurfaceCard>
          <p className="text-sm text-slate-700">
            {article.status === 'archived'
              ? 'Restore this article to a draft before editing it.'
              : 'You cannot edit this article.'}
          </p>
          <Link to={`/help/${article.slug}`} className="mt-4 inline-block">
            <Button variant="secondary">Back to article</Button>
          </Link>
        </SurfaceCard>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={mode === 'edit' ? 'Edit Help Article' : 'New Help Article'}
        subtitle={editingPublished
          ? 'Saving keeps this article published'
          : 'Draft internal knowledge base content with Markdown'}
      />

      <SurfaceCard>
        {noCategories ? (
          <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-semibold">No categories yet</div>
            <p className="mt-1">A category is required before an article can be saved.</p>
            {canManageHelpHub() ? (
              <Link to="/settings" className="mt-2 inline-block font-medium text-amber-950 underline">
                Open Settings → Help Hub
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm font-medium text-slate-700">
            Title
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Category
            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700">
          Excerpt
          <input
            value={excerpt}
            onChange={(event) => setExcerpt(event.target.value)}
            maxLength={500}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="mt-4 block text-sm font-medium text-slate-700">
          Tags
          <input
            value={tags}
            onChange={(event) => setTags(event.target.value)}
            placeholder="boarding, pricing, troubleshooting"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        {mode === 'edit' ? (
          <label className="mt-4 block text-sm font-medium text-slate-700">
            Change note
            <input
              value={changeNote}
              onChange={(event) => setChangeNote(event.target.value)}
              placeholder="Briefly describe what changed..."
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        ) : null}
        <div className="mt-5" data-color-mode="light">
          <label className="mb-2 block text-sm font-medium text-slate-700">Body</label>
          <MDEditor value={body} onChange={(value) => setBody(value ?? '')} height={420} preview="edit" />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => saveMutation.mutate()} disabled={disabled || saveMutation.isPending}>
            {editingPublished ? 'Save' : 'Save Draft'}
          </Button>
          {editingPublished ? null : (
            <Button variant="secondary" onClick={() => submitMutation.mutate()} disabled={disabled || submitMutation.isPending}>
              Submit
            </Button>
          )}
          {canPublish && !editingPublished ? (
            <Button variant="secondary" onClick={() => publishMutation.mutate()} disabled={disabled || publishMutation.isPending}>
              Publish
            </Button>
          ) : null}
          <Link to="/help"><Button variant="ghost">Cancel</Button></Link>
        </div>
      </SurfaceCard>
    </div>
  )
}

export function ArticleReviewQueuePage() {
  const { hasRole } = usePermissions()
  const { data, isLoading } = useQuery({
    queryKey: ['help-review-queue'],
    queryFn: () => helpApi.listArticles({ review_queue: 1, per_page: 50 }),
    enabled: hasRole(['management', 'super_admin']),
  })

  if (!hasRole(['management', 'super_admin'])) {
    return <SurfaceCard><p className="py-12 text-center text-sm text-slate-500">Article Queue is available to management only.</p></SurfaceCard>
  }

  const articles = data?.data ?? []

  return (
    <div className="space-y-6">
      <PageHeader title="Article Queue" subtitle="Approve or reject submitted Help Hub articles" />
      <SurfaceCard>
        {isLoading ? (
          <p className="py-8 text-center text-sm text-slate-500">Loading the Article Queue...</p>
        ) : articles.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No articles are waiting in the Article Queue.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {articles.map((article) => (
              <Link key={article.id} to={`/help/${article.slug}`} className="block py-4 hover:bg-slate-50">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{article.title}</div>
                    {article.ai_draft ? <div className="mt-1 text-xs font-semibold text-amber-800">AI draft</div> : null}
                    <div className="mt-1 text-sm text-slate-500">
                      {article.author?.name ?? 'Unknown'} · {article.category?.name ?? 'Uncategorized'}
                    </div>
                  </div>
                  <StatusPill status={article.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </SurfaceCard>
    </div>
  )
}

export function HelpCategoriesPage() {
  const queryClient = useQueryClient()
  const { hasRole } = usePermissions()
  const { data: categories = [] } = useCategories()
  const [editing, setEditing] = useState<ArticleCategory | null>(null)
  const [form, setForm] = useState({ name: '', slug: '', description: '', icon: 'HelpCircle', sort_order: '10' })

  const reset = () => {
    setEditing(null)
    setForm({ name: '', slug: '', description: '', icon: 'HelpCircle', sort_order: '10' })
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { ...form, sort_order: Number(form.sort_order || 0) }
      return editing ? helpApi.updateCategory(editing.id, payload) : helpApi.createCategory(payload)
    },
    onSuccess: () => {
      reset()
      queryClient.invalidateQueries({ queryKey: ['help-categories'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => helpApi.deleteCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['help-categories'] }),
  })

  if (!hasRole('super_admin')) {
    return <SurfaceCard><p className="py-12 text-center text-sm text-slate-500">Only super admins can manage Help Hub categories.</p></SurfaceCard>
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Help Categories" subtitle="Manage Help Hub taxonomy and sidebar icons" />
      <SurfaceCard>
        <CardTitle>{editing ? 'Edit Category' : 'New Category'}</CardTitle>
        <div className="mt-4 grid gap-4 md:grid-cols-5">
          <input aria-label="Category name" className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <input aria-label="Category slug" className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Slug" value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} />
          <input aria-label="Category icon" className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Icon" value={form.icon} onChange={(event) => setForm({ ...form, icon: event.target.value })} />
          <input aria-label="Category sort order" className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Sort" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} />
          <div className="flex gap-2">
            <Button onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isPending}>Save</Button>
            {editing ? <Button variant="ghost" onClick={reset}>Cancel</Button> : null}
          </div>
        </div>
        <input aria-label="Category description" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
      </SurfaceCard>

      <SurfaceCard>
        <CardTitle>Categories</CardTitle>
        <div className="mt-4 divide-y divide-slate-100">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                  <CategoryIcon category={category} />
                </span>
                <div>
                  <div className="font-medium text-slate-900">{category.name}</div>
                  <div className="text-xs text-slate-500">{category.slug} · sort {category.sort_order}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => {
                  setEditing(category)
                  setForm({
                    name: category.name,
                    slug: category.slug,
                    description: category.description ?? '',
                    icon: category.icon ?? 'HelpCircle',
                    sort_order: String(category.sort_order),
                  })
                }}>Edit</Button>
                <Button variant="ghost" onClick={() => deleteMutation.mutate(category.id)}>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </SurfaceCard>
    </div>
  )
}
